import { generatePath } from 'react-router-dom';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import {
  ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES,
  ACTIVE_ACCOUNT_COOKIE_NAME,
  AUTH_TOKENS_COOKIE_ATTRIBUTES,
  AUTH_TOKENS_COOKIE_NAME,
} from '@common/constants';
import { EAppRoutes, Permission, UserIdentityProvider } from '@common/enums';
import appConfig from '@configs/app-config';
import { AuthTokensCookie, DecodedToken, UserScope } from '../../hooks/auth/interfaces';

const authApiBasePath = appConfig.bffAuthApiUrl;

/**
 * Checks if the token is expired.
 * @param token
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp }: DecodedToken = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch (error) {
    console.error('Error decoding the token:', error);
    return true;
  }
};

/**
 * Returns new access tokens with a valid refresh token.
 * @param refreshToken
 */
export const refreshToken = async (refreshToken: string): Promise<AuthTokensCookie | null> => {
  try {
    const response = await fetch(`${authApiBasePath}/refresh-access-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Refresh token request failed with status ' + response.status);
    }

    return await response.json();
  } catch (error) {
    console.error('Error refreshing access token:', error);
    throw error;
  }
};

/**
 * Helper function to update the cookie with user scope and new access token
 */
export const updateCookieWithUserScope = async (
  currentCookie: AuthTokensCookie,
  accessToken: string
): Promise<void> => {
  const newCookie: AuthTokensCookie = {
    ...currentCookie,
    access_token: accessToken,
  };
  Cookies.set(AUTH_TOKENS_COOKIE_NAME, JSON.stringify(newCookie), AUTH_TOKENS_COOKIE_ATTRIBUTES);
};

/**
 * Checks if the token is valid. First checks if the access token is expired. If it is, tries to update tokens using
 * refresh token.
 */
export const isTokenValid = async (): Promise<boolean> => {
  const cookieString = Cookies.get(AUTH_TOKENS_COOKIE_NAME);

  // If there is no cookie, the token is not valid
  if (!cookieString) {
    return false;
  }

  // If there is a cookie, parse it
  const cookie: AuthTokensCookie = JSON.parse(cookieString);
  const { access_token, refresh_token, user_scope } = cookie;

  // If the access token is not expired, it is valid
  if (!isTokenExpired(access_token)) {
    // fetch user scope if it hasn't been retrieved already
    if (!user_scope) {
      await updateCookieWithUserScope(cookie, access_token);
    }

    return true;
  }

  // If the access token is expired, try to update tokens using refresh token and refetch scope
  if (refresh_token) {
    const newToken = await refreshToken(refresh_token);
    await updateCookieWithUserScope(cookie, newToken.access_token);
    return true;
  }

  return false;
};

/**
 * Login on dev (local) environment is managed by making a separate call to the server when the `state` and `code`
 * search query parameters are passed in the URL. This is done because the Okta login flow is not supported on dev
 * directly.
 * @param state
 * @param code
 */
export const runDevLogin = async (state: string, code: string): Promise<void> => {
  let response = null;

  const identityProvider = getUserIdentityProviderFromState(state);
  if (identityProvider === UserIdentityProvider.Okta) {
    response = await fetch(
      `https://activate2-nfe.axonixtest.com/v1/auth/okta-local/oidc-callback?state=${state}&code=${code}&response_type=BODY`
    );
  } else if (identityProvider === UserIdentityProvider.Auth0) {
    response = await fetch(
      `https://activate2-nfe.axonixtest.com/v1/auth/auth0-local/oidc-callback?state=${state}&code=${code}&response_type=BODY`
    );
  }
  if (response && response.ok) {
    const responseBody = await response.json();
    const { access_token, refresh_token }: AuthTokensCookie = responseBody;
    Cookies.remove(AUTH_TOKENS_COOKIE_NAME, AUTH_TOKENS_COOKIE_ATTRIBUTES);
    Cookies.remove(ACTIVE_ACCOUNT_COOKIE_NAME, ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES);
    Cookies.set(
      AUTH_TOKENS_COOKIE_NAME,
      JSON.stringify({ access_token, refresh_token }),
      AUTH_TOKENS_COOKIE_ATTRIBUTES
    );
  }
};

/**
 * Retrieve user scope from cookie.
 */
export const getUserScopeFromCookie = (): UserScope => {
  const cookieString = Cookies.get(AUTH_TOKENS_COOKIE_NAME);
  // If there is no cookie, the token is not valid
  if (!cookieString) {
    return null;
  }

  const cookie: AuthTokensCookie = JSON.parse(cookieString);
  return cookie.user_scope;
};

/**
 * Retrieve A1 Application Key from URL.
 */
export const getApiKeyFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('applicationKey');
};

/**
 * Generate state with provider
 */
export const generateState = (provider: UserIdentityProvider): string => {
  const csrfToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  const stateObj = {
    csrfToken: csrfToken,
    userIdentityProvider: provider,
  };
  return btoa(JSON.stringify(stateObj)); // Base64 encode the JSON string
};

/**
 * Get the OIDC provider from the state
 */
export const getUserIdentityProviderFromState = (state: string): UserIdentityProvider => {
  try {
    const stateObj = JSON.parse(atob(state));

    return stateObj.userIdentityProvider;
  } catch (error) {
    return null;
  }
};

/**
 * Get the auth tokens cookie
 */
export const getAuthTokensCookie = (): AuthTokensCookie => {
  try {
    return JSON.parse(Cookies.get(AUTH_TOKENS_COOKIE_NAME));
  } catch (error) {
    return null;
  }
};

/** Check if user has permissions
 * @param userScope
 * @param permission
 */
export const userScopeHasPermissions = (userScope: UserScope, permission: Permission): boolean => {
  return userScope?.permission_ids?.includes(permission);
};

/**
 * Redirect to home if user is not authorized
 * @pacram requiredPermissions
 */
export const redirectToHomeIfUnauthorized = (authContext, requiredPermission, navigate): void => {
  if (!authContext.checkAuthAndPermissions(requiredPermission)) {
    if (authContext.isAuthenticated) {
      // For MVP, redirect to Reporting until Home is made available
      navigate(generatePath(EAppRoutes.Reporting));
    } else {
      navigate(generatePath(EAppRoutes.Home));
    }
  }
};

export const fetchUserScope = async (): Promise<UserScope | null> => {
  try {
    const tokenIsValid = await isTokenValid();
    if (tokenIsValid) {
      const cookieString = Cookies.get(AUTH_TOKENS_COOKIE_NAME);
      const currentCookie: AuthTokensCookie = JSON.parse(cookieString);
      const { access_token } = currentCookie;

      const response = await fetch(`${authApiBasePath}/user-scope`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: access_token }),
      });
      if (!response.ok) {
        throw new Error('User scope request failed with status ' + response.status);
      } else {
        const userScope = await response.json();
        if (!userScope.permission_ids || userScope.permission_ids.length === 0) {
          throw new Error('Invalid permissions');
        }
        const newCookie: AuthTokensCookie = {
          ...currentCookie,
          access_token: access_token,
          user_scope: userScope,
        };
        Cookies.set(AUTH_TOKENS_COOKIE_NAME, JSON.stringify(newCookie), AUTH_TOKENS_COOKIE_ATTRIBUTES);
        return userScope;
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch user scope:', error);
    Cookies.remove(AUTH_TOKENS_COOKIE_NAME, AUTH_TOKENS_COOKIE_ATTRIBUTES);
    Cookies.remove(ACTIVE_ACCOUNT_COOKIE_NAME, ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES);
    return null;
  }
};
